import { useQuery } from '@tanstack/react-query';
import { TrashIcon, ExclamationTriangleIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { usersService } from '@/services/users.service';
import { executiveProtectionService } from '@/services/executive-protection.service';

export interface AgentSelection {
  agentIds: string[];
  leadAgentId: string | null;
}

interface AgentMultiSelectProps {
  value: AgentSelection;
  onChange: (value: AgentSelection) => void;
  /** datetime-local or ISO string; conflicts are only checked when present */
  startDatetime?: string;
  endDatetime?: string;
  /** Exclude this activity from conflict checks (edit mode) */
  excludeActivityId?: string;
}

const formatConflictTime = (iso?: string): string => {
  if (!iso) { return ''; }
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export default function AgentMultiSelect({
  value,
  onChange,
  startDatetime,
  endDatetime,
  excludeActivityId,
}: AgentMultiSelectProps) {
  // Assignable agents: active users with at least one non-VIEWER role
  const { data: usersData } = useQuery({
    queryKey: ['users', 'assignable-agents'],
    queryFn: () => usersService.getUsers({ isActive: true, limit: 200 }),
  });
  // `roles` is field-permission gated (ADMIN+ only); when absent we cannot
  // exclude viewers client-side, so include the user rather than crash.
  const assignableUsers = (usersData?.users ?? []).filter(
    (u) => !u.roles || u.roles.some((r) => r.name !== 'VIEWER')
  );

  const userById = new Map(assignableUsers.map((u) => [u.id, u]));

  // Non-blocking double-booking check (server-side via conflictDetection.service)
  const { data: conflictResult } = useQuery({
    queryKey: [
      'executive-protection',
      'agent-conflicts',
      value.agentIds,
      startDatetime,
      endDatetime,
      excludeActivityId,
    ],
    queryFn: () =>
      executiveProtectionService.checkActivityConflicts({
        agentIds: value.agentIds,
        startDatetime: new Date(startDatetime as string).toISOString(),
        endDatetime: endDatetime ? new Date(endDatetime).toISOString() : undefined,
        excludeActivityId,
      }),
    enabled: value.agentIds.length > 0 && Boolean(startDatetime),
  });
  const agentConflicts = (conflictResult?.conflicts ?? []).filter(
    (c) => c.conflictType === 'AGENT_DOUBLE_BOOKED'
  );

  const handleAdd = (userId: string) => {
    if (value.agentIds.includes(userId)) { return; }
    const agentIds = [...value.agentIds, userId];
    onChange({
      agentIds,
      // First selected agent defaults to lead
      leadAgentId: value.leadAgentId ?? agentIds[0],
    });
  };

  const handleRemove = (userId: string) => {
    const agentIds = value.agentIds.filter((id) => id !== userId);
    if (agentIds.length === 0) {
      onChange({ agentIds, leadAgentId: null });
      return;
    }
    onChange({
      agentIds,
      // Removing the lead promotes the first remaining agent
      leadAgentId: value.leadAgentId === userId ? agentIds[0] : value.leadAgentId,
    });
  };

  const handleMakeLead = (userId: string) => {
    onChange({ ...value, leadAgentId: userId });
  };

  return (
    <div>
      {/* Selected agents */}
      {value.agentIds.length > 0 && (
        <div className="mb-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800/50">
          <div className="space-y-2">
            {value.agentIds.map((userId) => {
              const user = userById.get(userId);
              const isLead = value.leadAgentId === userId;
              return (
                <div
                  key={userId}
                  data-testid="agent-row"
                  className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700"
                >
                  <div className="flex items-center space-x-2">
                    {isLead && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300 rounded">
                        TEAM LEAD
                      </span>
                    )}
                    <span className="font-medium">
                      {user ? `${user.firstName} ${user.lastName}` : 'Unknown user'}
                    </span>
                    {user && (user.roles?.length ?? 0) > 0 && (
                      <span className="text-sm text-gray-500">- {user.roles?.[0]?.name}</span>
                    )}
                  </div>
                  <div className="flex items-center space-x-1">
                    {!isLead && (
                      <button
                        type="button"
                        onClick={() => handleMakeLead(userId)}
                        className="px-2 py-1 text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                        title="Make team lead"
                      >
                        <ShieldCheckIcon className="w-4 h-4 inline mr-1" />
                        Make lead
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleRemove(userId)}
                      className="p-1 text-red-500 hover:text-red-700 dark:hover:text-red-400"
                      title="Remove agent"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Dropdown to add agents */}
      <select
        value=""
        aria-label="Add security agent"
        onChange={(e) => {
          if (e.target.value) {
            handleAdd(e.target.value);
            e.target.value = '';
          }
        }}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800"
      >
        <option value="">+ Add security agent...</option>
        {assignableUsers
          .filter((u) => !value.agentIds.includes(u.id))
          .map((user) => (
            <option key={user.id} value={user.id}>
              {user.firstName} {user.lastName} ({user.email}) - {user.roles?.[0]?.name ?? ''}
            </option>
          ))}
      </select>

      {value.agentIds.length > 0 && (
        <p className="text-xs text-gray-500 mt-1">
          {value.agentIds.length} agent{value.agentIds.length > 1 ? 's' : ''} assigned. Exactly one
          agent is the team lead.
        </p>
      )}

      {/* Non-blocking double-booking warnings */}
      {agentConflicts.length > 0 && (
        <div className="mt-2 p-3 rounded-lg border border-warning-300 bg-warning-50 dark:border-warning-700 dark:bg-warning-900/20">
          {agentConflicts.map((conflict, index) => {
            const user = conflict.agentId ? userById.get(conflict.agentId) : undefined;
            const who = user ? `${user.firstName} ${user.lastName}` : 'An agent';
            const start = formatConflictTime(conflict.conflictingMissionStart);
            const end = formatConflictTime(conflict.conflictingMissionEnd);
            return (
              <p
                key={`${conflict.conflictingActivityId}-${conflict.agentId ?? index}`}
                className="text-sm text-warning-800 dark:text-warning-300 flex items-start"
              >
                <ExclamationTriangleIcon className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" />
                {who} is on &ldquo;{conflict.conflictingMissionTitle}&rdquo;
                {start && ` ${start}`}
                {end && `–${end}`}. You can still save.
              </p>
            );
          })}
        </div>
      )}
    </div>
  );
}
